<script lang="ts">
    import { onDestroy, setContext } from 'svelte'
    import { derived as sderived, writable, type Writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import * as yup from 'yup'

    import type { CrumbArray } from 'hydria/components/breadcrumb'
    import { validateSyncAt } from 'hydria/document_helpers'
    import { HydriaDocument, type IDocument } from 'hydria/HydriaDocument'
    import type { IRevision } from 'hydria/HydriaDocument'
    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { spath } from 'hydria/svelte_helpers'

    import { INFORMATION_PAGE_TYPES } from 'admin/admin_constants'
    import { Breadcrumb } from 'breadcrumb'

    import DocumentEdit from 'hydria/components/DocumentEdit.svelte'
    import Dropdown from 'hydria/components/Dropdown.svelte'
    import GenTextInput from 'hydria/components/Generics/GenTextInput.svelte'
    import LanguageSelector from 'hydria/components/Generics/LanguageSelector.svelte'
    import IntroHtml from 'hydria/field_components/IntroHtml.svelte'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    interface Props {
        route: any
    }

    let { route }: Props = $props()
    const revFields = ['title', 'content_html', 'lg', 'page']

    interface ILocalDocument extends IDocument {}
    interface ILocalRevision extends IRevision {
        title: string
        content_html: string
        page: string
    }

    class LocalDocument extends HydriaDocument {
        docType = 'information'
        docTypePlural = 'informations'

        docTypeName = tgettext('Information')
        docTypeNamePlural = tgettext('Informations')
    }

    const Document = new LocalDocument()
    const status = sderived(Document.status, ($status) => $status)

    let inDocId = $derived(route.result.path.params?.docId)
    let inRevId = $derived(route.result.path.params?.revId)

    const revSaved = sderived(Document.revision, ($rev) => (!$rev ? $rev : Object.fromEntries(Object.entries($rev).filter(([k, v]) => revFields.includes(k)))) as ILocalRevision)
    const docSaved = sderived(Document.document, ($rev) => ({ ...$rev }) as ILocalDocument)

    // revEdit is actually handled in DocumentEdit.
    let revEdit: Writable<ILocalRevision | undefined> = writable(undefined)
    setContext('revEdit', revEdit)

    const revSchema = yup.object().shape({
        title: yup.string().required(),
        content_html: yup.string().required(),
        lg: yup
            .string()
            .required()
            .test('lgIsValid', '${path} is not valid', (value) => value === 'fr' || value === 'en'),
    })
    let formIsValid = $derived(revSchema.isValidSync($revEdit))

    const pageTypeIndex = Object.fromEntries(INFORMATION_PAGE_TYPES.map((inf) => [inf.id, inf.name]))
    let fakeSelectedPage: { id: string; name: string } | undefined = $state()

    unsubs.push(
        revEdit.subscribe(($revEdit) => {
            if ($revEdit) {
                fakeSelectedPage = INFORMATION_PAGE_TYPES.filter((v) => v.id === $revEdit.page).pop()
            }
        }),
    )

    let isPristine = $derived($revEdit && $revSaved && $revEdit.page === $revSaved.page)

    let statusIcon: string = $derived(isPristine ? (pageTypeIndex[$revSaved.page] != null ? 'mdi:check-circle' : 'mdi:alert') : $revEdit && pageTypeIndex[$revEdit.page] != null ? 'mdi:undo' : 'mdi:undo')
    let statusClass: string = $derived(isPristine ? (pageTypeIndex[$revSaved.page] != null ? 'btn-link' : 'btn-danger') : $revEdit && pageTypeIndex[$revEdit.page] != null ? 'btn-outline-warning' : 'btn-outline-danger')

    const crumbs = sderived(status, ($status) => {
        const c: CrumbArray = [{ txt: Document.docTypeNamePlural, url: spath(`/${Document.docTypePlural.replace('_', '-')}`) }]
        switch ($status) {
            case 'loading':
                c.push({
                    txt: '…',
                })
                break
            case 'error':
            case 'not-found':
                c.push({
                    icon: 'mdi:alert',
                })
                break
            case 'ready':
                c.push({
                    txt: tgettext('Edit'),
                })
                c.push({
                    txt: `<span class="text-muted">${Document.docId}</span>`,
                    html: true,
                })
        }
        return c
    })
</script>

<Breadcrumb {crumbs} />

<DocumentEdit {Document} docId={inDocId} revId={inRevId} {formIsValid} {revFields}>
    {#if $revEdit}
        <div class="card card-content">
            <div class="card-header text-bg-primary">{$_('Document')}</div>
            <div class="card-body d-grid gap-2">
                <GenTextInput label={tgettext('Titre')} saved={$revSaved.title} bind:value={$revEdit.title} isValid={(v) => validateSyncAt(revSchema, 'title', { title: v })} helpText={tgettext('Ce texte est utilisé pour le titre de la page, dans la barre du navigateur.')} disabled={$status != 'ready'} />

                <LanguageSelector bind:value={$revEdit.lg} />

                <div class="row g-3 align-items-baseline">
                    <div class="col-2 text-end">
                        <label for="f-nil" class="col-form-label">{$_('Page to display in')}</label>
                    </div>
                    <div class="col">
                        <Dropdown onSelect={(e) => ($revEdit.page = e.id)} selected={fakeSelectedPage} items={INFORMATION_PAGE_TYPES} placeholder="" text={(sitem) => sitem.name} selectedText={(sitem) => sitem.name} />
                    </div>
                    <div class="col-auto text-end">
                        <button
                            type="button"
                            class="btn btn-sm {statusClass}"
                            disabled={isPristine}
                            onclick={() => {
                                $revEdit.page = $revSaved.page
                            }}><Icon inline icon={statusIcon} /></button
                        >
                    </div>
                </div>

                <IntroHtml saved={$revSaved.content_html} bind:value={$revEdit.content_html} disabled={$status != 'ready'} label={tgettext('Contenu')} helpText={undefined} isValid={(v) => validateSyncAt(revSchema, 'content_html', { content_html: v })} />
            </div>
        </div>
    {/if}
</DocumentEdit>
